import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEcomRecommendationsV1Recommendation from './ecom-recommendations-v1-recommendation-recommendations.http.js';

export interface Recommendation {
  /**
   * Recommended items.
   * @maxSize 100
   */
  items?: CatalogReference[];
  /** The algorithm used to provide the recommendation. */
  algorithm?: Algorithm;
}

/** Used for grouping line items. Sent when an item is added to a cart, checkout, or order. */
export interface CatalogReference {
  /**
   * ID of the item within the catalog it belongs to.
   * @minLength 1
   * @maxLength 36
   */
  catalogItemId?: string;
  /**
   * ID of the app providing the catalog.
   *
   * You can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).
   *
   * For items from Wix catalogs, the following values always apply:
   * + Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`
   * + Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`
   * + Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`
   * @minLength 1
   */
  appId?: string;
  /**
   * Additional item details in `key:value` pairs.
   *
   * Use this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.
   *
   * For Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)
   * or [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).
   */
  options?: Record<string, any> | null;
}

export interface Algorithm {
  /**
   * ID of the algorithm, as defined by the app that provides it.
   * @format GUID
   */
  _id?: string;
  /**
   * ID of the app providing the algorithm. This can be an app built by Wix or a 3rd-party app.
   *
   * See app IDs for [apps built by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).
   * @minLength 1
   * @maxLength 36
   */
  appId?: string;
}

export interface ListAvailableAlgorithmsRequest {}

export interface ListAvailableAlgorithmsResponse {
  /**
   * List of algorithms available for use on a site.
   * @maxSize 500
   */
  availableAlgorithms?: AlgorithmInfo[];
}

export interface AlgorithmInfo {
  /** Algorithm configuration. */
  config?: AlgorithmConfig;
  /**
   * ID of the app that provides the algorithm. This can be an app built by Wix or a 3rd-party app.
   *
   * See app IDs for [apps built by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).
   * @minLength 1
   * @maxLength 36
   */
  appId?: string;
  /**
   * IDs of apps with catalogs that this algorithm supports. This can be an app built by Wix or a 3rd-party app.
   *
   * See app IDs for [apps built by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).
   * @minSize 1
   * @maxSize 100
   * @minLength 1
   * @maxLength 36
   */
  catalogAppIds?: string[];
}

export interface AlgorithmConfig {
  /**
   * Algorithm name. This value is not translatable.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /**
   * Algorithm description. This describes how the algorithm works and if it has any limitations regarding site content, number of items in the catalog, site traffic, and so on. This value is not translatable.
   * @minLength 1
   * @maxLength 500
   */
  description?: string;
  /**
   * A supplemental `description`. It can be used to help break up and organize information. You can, for example, display this information as a tooltip or as an additional section that is collapsed by default.
   * @minLength 1
   * @maxLength 300
   */
  additionalInfo?: string | null;
  /**
   * Algorithms may have the following types:
   * * `RELATED_ITEMS` - This type of algorithm provides recommendations based on 1 or more other provided items. For example, when an item is added to a cart, the algorithm can suggest other items frequently bought together with that item.
   * * `GLOBAL` - This type of algorithm provides general recommendations based on site or project statistics. For example, bestsellers or new arrivals.
   */
  algorithmType?: AlgorithmTypeWithLiterals;
  /**
   * Algorithm ID. This must be unique for a specific app but does not have to be unique across all apps on the site or in the project.
   * @format GUID
   */
  algorithmId?: string;
}

export enum AlgorithmType {
  UNSPECIFIED = 'UNSPECIFIED',
  RELATED_ITEMS = 'RELATED_ITEMS',
  GLOBAL = 'GLOBAL',
}

/** @enumType */
export type AlgorithmTypeWithLiterals =
  | AlgorithmType
  | 'UNSPECIFIED'
  | 'RELATED_ITEMS'
  | 'GLOBAL';

export interface GetRecommendationRequest {
  /**
   * Catalog items to base recommendations on. Required if the algorithm's `algorithmType` is `RELATED_ITEMS`.
   * @maxSize 300
   */
  items?: CatalogReference[];
  /**
   * Algorithms to specify for generating recommendations, ordered by preference. The method processes algorithms in order and provides recommendations from the first algorithm that meets the `minimumRecommendedItems` threshold.
   * @minSize 1
   * @maxSize 10
   */
  algorithms: Algorithm[];
  /**
   * Minimum number of items an algorithm must provide for its recommendations to be returned in the response. If no algorithm provides enough items, an empty array is returned.
   * @max 100
   */
  minimumRecommendedItems?: number;
}

export interface GetRecommendationResponse {
  /** The recommended items and the algorithm that provided them. Empty if no algorithm met the `minimumRecommendedItems` threshold. */
  recommendation?: Recommendation;
}

export interface ItemAppIdNotSupportedByProvider {
  /**
   * Items with an App ID not supported by the provider. Supported App IDs can be found in the provider config in the app dashboard.
   * @minSize 1
   * @maxSize 300
   */
  items?: CatalogReference[];
  /**
   * Algorithms that don't support the requested items.
   * @minSize 1
   * @maxSize 10
   */
  algorithms?: Algorithm[];
}

export interface RecommendationAlgorithmNotSupported {
  /**
   * Algorithms not supported by the provider.
   * @minSize 1
   * @maxSize 10
   */
  unsupportedAlgorithms?: Algorithm[];
}

/** @docsIgnore */
export type GetRecommendationApplicationErrors =
  | {
      code?: 'ITEM_APP_ID_NOT_SUPPORTED';
      description?: string;
      data?: ItemAppIdNotSupportedByProvider;
    }
  | {
      code?: 'ALGORITHM_NOT_SUPPORTED';
      description?: string;
      data?: RecommendationAlgorithmNotSupported;
    }
  | {
      code?: 'RECOMMENDATIONS_PROVIDER_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };

/**
 * Returns a list of recommendation algorithms that can be used on your Wix site or project. These algorithms can be used with [`getRecommendation()`](#getRecommendation) to provide item recommendations to the customer.
 *
 * Algorithms are run by the apps that provide them, and can only be used on catalogs they support. Apps may provide algorithms for use with their own catalogs and/or catalogs from other apps.
 *
 * The app which provides an algorithm is referenced by that algorithm’s `appId`. The apps whose catalogs are supported by an algorithm are referenced by the IDs in that algorithm’s `catalogAppIds` array.
 *
 *
 * For an algorithm to be considered “Available” and returned in this method’s response, the algorithm must meet the following conditions:
 * 1. The algorithm’s `appId` must match the ID of an installed Wix app.
 * 2. At least 1 of the IDs in `catalogAppIds` must match the ID of an installed Wix app.
 *
 * Wix app IDs are [listed here](https://dev.wix.com/docs/api-reference/articles/get-started/apps-created-by-wix).
 * @public
 * @permissionId RECOMMENDATIONS.READ
 * @applicableIdentity APP
 * @fqn com.wix.ecom.recommendations.v1.RecommendationsService.ListAvailableAlgorithms
 */
export async function listAvailableAlgorithms(): Promise<
  NonNullablePaths<
    ListAvailableAlgorithmsResponse,
    | `availableAlgorithms`
    | `availableAlgorithms.${number}.config.name`
    | `availableAlgorithms.${number}.config.description`
    | `availableAlgorithms.${number}.config.algorithmType`
    | `availableAlgorithms.${number}.config.algorithmId`
    | `availableAlgorithms.${number}.appId`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixEcomRecommendationsV1Recommendation.listAvailableAlgorithms(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Returns a recommendation object containing a list of items to recommend to the customer.
 *
 * `getRecommendation()` determines which items to recommend based on the given recommendation algorithms.
 *
 * `getRecommendation()` doesn’t run the algorithms. It calls the installed apps that provide them.
 *
 * Apps may provide algorithms for use with their own catalogs, or for use with catalogs from other apps.
 * For example, Wix Stores provides algorithms that can only be used on its own catalogs.
 * To run an algorithm, the app providing it must be installed, and an app providing a supported catalog must be installed.
 * For more information and to see which algorithms are available on your site or project, call [`listAvailableAlgorithms()`](#listavailablealgorithms).
 *
 * `getRecommendation()` operates as follows:
 * 1. `getRecommendation()` receives as input a list of algorithms as an array. These algorithms can be provided by different apps and can apply to different catalogs.
 * 2. `getRecommendation()` calls the app that corresponds to the `appId` of the first algorithm in the list of algorithms. It passes that algorithm’s ID and the IDs of any subsequent algorithms in the array for the same app.
 * 3. The app runs the algorithms.
 * 4. `getRecommendation()` returns items recommendations from the first algorithm (according to its position in the `algorithms` array) that meets the minimum number of recommendations. At that point `getRecommendation()` stops calling other apps.
 * 5. If none of the algorithms run by the first app meet the minimum recommended items, `getRecommendation()` finds the next algorithm in the array with a new `appId` (an ID of an app that has not yet been called), and repeats the process.
 * 6. If no algorithms in the `algorithms` array recommend at least the minimum recommended items, `getRecommendation()` returns an empty array.
 * @param algorithms - Algorithms to specify for generating recommendations, ordered by preference. The method processes algorithms in order and provides recommendations from the first algorithm that meets the `minimumRecommendedItems` threshold.
 * @public
 * @requiredField algorithms
 * @requiredField algorithms._id
 * @requiredField algorithms.appId
 * @requiredField options.items.appId
 * @requiredField options.items.catalogItemId
 * @param options - Get recommendation options.
 * @permissionId RECOMMENDATIONS.READ
 * @applicableIdentity APP
 * @fqn com.wix.ecom.recommendations.v1.RecommendationsService.GetRecommendation
 */
export async function getRecommendation(
  algorithms: NonNullablePaths<Algorithm, `_id` | `appId`, 2>[],
  options?: NonNullablePaths<
    GetRecommendationOptions,
    `items.${number}.appId` | `items.${number}.catalogItemId`,
    4
  >
): Promise<
  NonNullablePaths<
    GetRecommendationResponse,
    | `recommendation.items`
    | `recommendation.items.${number}.catalogItemId`
    | `recommendation.items.${number}.appId`
    | `recommendation.algorithm._id`
    | `recommendation.algorithm.appId`,
    5
  > & {
    __applicationErrorsType?: GetRecommendationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    algorithms: algorithms,
    items: options?.items,
    minimumRecommendedItems: options?.minimumRecommendedItems,
  });

  const reqOpts =
    ambassadorWixEcomRecommendationsV1Recommendation.getRecommendation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          algorithms: '$[0]',
          items: '$[1].items',
          minimumRecommendedItems: '$[1].minimumRecommendedItems',
        },
        singleArgumentUnchanged: false,
      },
      ['algorithms', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetRecommendationOptions {
  /**
   * Catalog items to base recommendations on. Required if the algorithm's `algorithmType` is `RELATED_ITEMS`.
   * @maxSize 300
   */
  items?: CatalogReference[];
  /**
   * Minimum number of items an algorithm must provide for its recommendations to be returned in the response. If no algorithm provides enough items, an empty array is returned.
   * @max 100
   */
  minimumRecommendedItems?: number;
}
